import { get } from './client'

// ── Executive Intelligence types (mirrors engine modules/executive/types.ts) ──

export type AlertSeverity = 'critical' | 'warning' | 'info'

export type ExecAlert = {
  id:              string
  title:           string
  description:     string
  severity:        AlertSeverity
  priority:        1 | 2 | 3
  module:          string
  affectedMetric:  string
  rootCause:       string
  suggestedAction: string
  owner:           string
  value:           number | null
  threshold:       number | null
  detectedAt:      string
}

export type ExecInsight = {
  id:           string
  module:       string
  text:         string
  type:         'positive' | 'negative' | 'neutral'
  metric:       string
  delta:        number | null
  dimension:    string | null
  dimensionKey: string | null
  confidence:   number
  generatedAt:  string
}

export type ExecOpportunity = {
  id:              string
  type:            string
  title:           string
  description:     string
  estimatedImpact: number | null
  urgency:         'high' | 'medium' | 'low'
  module:          string
  affectedItems:   string[]
  detectedAt:      string
}

export type ScorecardKpi = {
  id:             string
  name:           string
  unit:           string
  value:          number | null
  formattedValue: string
  target:         number | null
  achievementPct: number | null
  status:         string
  trend:          string
  trendMagnitude: number
  importance:     number
  riskLevel:      'high' | 'medium' | 'low' | null
}

export type ExecScorecard = {
  module:       string
  moduleLabel:  string
  overallScore: number
  kpis:         ScorecardKpi[]
  period:       string
  dateFrom:     string
  dateTo:       string
  generatedAt:  string
}

export type HeatmapCell = { label: string; value: number; count: number; intensity: number }
export type HeatmapRow  = { key: string; label: string; cells: HeatmapCell[] }

export type ExecHeatmap = {
  type:         string
  metric:       string
  unit:         string
  columnLabels: string[]
  rows:         HeatmapRow[]
  generatedAt:  string
}

export type RankingRow = { rank: number; key: string; label: string; value: number; unit: string; delta?: number }

export type ExecRanking = {
  id:          string
  title:       string
  dimension:   string
  direction:   'top' | 'bottom'
  rows:        RankingRow[]
  generatedAt: string
}

export type ComparisonMetric = {
  id:               string
  label:            string
  unit:             string
  current:          number | null
  previous:         number | null
  delta:            number | null
  deltaPct:         number | null
  trend:            'up' | 'down' | 'flat'
  betterWhenHigher: boolean
}

export type ExecComparison = {
  metrics:       ComparisonMetric[]
  currentLabel:  string
  previousLabel: string
  dateFrom:      string
  dateTo:        string
  prevDateFrom:  string
  prevDateTo:    string
  generatedAt:   string
}

export type TimelineEvent = {
  id:          string
  type:        string
  title:       string
  description: string
  module:      string
  severity:    string
  ts:          string
  value?:      number
}

export type ExecTimeline = {
  events:      TimelineEvent[]
  dateFrom:    string
  dateTo:      string
  generatedAt: string
}

export type ExecDashboard = {
  role:          string
  period:        string
  dateFrom:      string
  dateTo:        string
  summary:       Record<string, number | null>
  alerts:        ExecAlert[]
  insights:      ExecInsight[]
  opportunities: ExecOpportunity[]
  scorecards:    ExecScorecard[]
  rankings:      ExecRanking[]
  kpiRows:       ScorecardKpi[]
  generatedAt:   string
}

export type ExecRole = { id: string; label: string; modules: string[]; owners: string[] }

// ── API ────────────────────────────────────────────────────────────────────────

function q(shop: string, period?: string, extra?: Record<string, string>): string {
  const p = new URLSearchParams({ shop })
  if (period) p.set('period', period)
  if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v))
  return p.toString()
}

export const execApi = {
  getDashboard:    (shop: string, role: string, period = 'this_month'): Promise<ExecDashboard> =>
    get<ExecDashboard>(`/executive/dashboard?${q(shop, period, { role })}`),

  getAlerts:       (shop: string, period = 'this_month'): Promise<ExecAlert[]> =>
    get<ExecAlert[]>(`/executive/alerts?${q(shop, period)}`),

  getInsights:     (shop: string, period = 'this_month'): Promise<ExecInsight[]> =>
    get<ExecInsight[]>(`/executive/insights?${q(shop, period)}`),

  getOpportunities: (shop: string, period = 'this_month'): Promise<ExecOpportunity[]> =>
    get<ExecOpportunity[]>(`/executive/opportunities?${q(shop, period)}`),

  getScorecards:   (shop: string, period = 'this_month', module?: string): Promise<ExecScorecard[]> =>
    get<ExecScorecard[]>(`/executive/scorecards?${q(shop, period, module ? { module } : {})}`),

  getHeatmap:      (shop: string, type: string, metric: string, period = 'this_month'): Promise<ExecHeatmap> =>
    get<ExecHeatmap>(`/executive/heatmap?${q(shop, period, { type, metric })}`),

  getRankings:     (shop: string, period = 'this_month'): Promise<ExecRanking[]> =>
    get<ExecRanking[]>(`/executive/rankings?${q(shop, period)}`),

  getComparison:   (shop: string, period = 'this_month'): Promise<ExecComparison> =>
    get<ExecComparison>(`/executive/comparison?${q(shop, period)}`),

  getTimeline:     (shop: string, period = 'rolling_7'): Promise<ExecTimeline> =>
    get<ExecTimeline>(`/executive/timeline?${q(shop, period)}`),

  getFinancialScorecard: (shop: string): Promise<ExecScorecard & { financialDetail: Record<string, unknown> }> =>
    get(`/executive/financial-scorecard?shop=${encodeURIComponent(shop)}`),

  getRoles:        (): Promise<ExecRole[]> =>
    get<ExecRole[]>('/executive/roles?shop=_'),
}
